"use client";

import React, { useCallback, useRef, useState } from "react";
import styles from "./similar-properties.module.css";
import { ListingCardData } from "@/types/types";
import { MdOutlineChevronLeft, MdOutlineChevronRight } from "react-icons/md";
import HomeListingCard from "@/components-v2/listing-cards/home-listing-card";

interface SimilarPropertiesProps {
  listings: ListingCardData[];
}

export default function SimilarProperties({
  listings,
}: SimilarPropertiesProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeftState, setScrollLeftState] = useState(0);
  const dragDistanceRef = useRef(0);

  if (!listings || listings.length === 0) return null;

  const handleScrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -340, behavior: "smooth" });
    }
  };

  const handleScrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 340, behavior: "smooth" });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    dragDistanceRef.current = 0; // Reset on new click
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeftState(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => setIsDragging(false);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();

    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;

    // Accumulate total distance dragged (using absolute values)
    dragDistanceRef.current = Math.abs(x - startX);

    scrollRef.current.scrollLeft = scrollLeftState - walk;
  };

  // 🚀 THE FIX: Catch the click event early and murder it if the user dragged the track
  const handleCaptureClick = useCallback((e: React.MouseEvent) => {
    if (dragDistanceRef.current > 5) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  return (
    <section className={styles.section}>
      {/* Clean Header (Title Only) */}
      <div className={styles.header}>
        <h2 className={styles.title}>Proprietăți similare în zonă</h2>
      </div>

      {/* Scroll Track */}
      <div
        className={`${styles.cardsTrack} ${isDragging ? styles.dragging : ""}`}
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onClickCapture={handleCaptureClick}
      >
        {listings.map((listing) => (
          <div key={listing.listing_id} className={styles.cardWrapper}>
            <HomeListingCard data={listing} />
          </div>
        ))}
      </div>

      {/* Carousel Controls (Moved to Bottom) */}
      <div className={styles.carouselControls}>
        <button
          className={styles.controlBtn}
          aria-label="Previous properties"
          onClick={handleScrollLeft}
        >
          <MdOutlineChevronLeft />
        </button>
        <button
          className={styles.controlBtn}
          aria-label="Next properties"
          onClick={handleScrollRight}
        >
          <MdOutlineChevronRight />
        </button>
      </div>
    </section>
  );
}
